import { AgentReportValue, formatAgentReportValue } from 'components/superreports/AgentReportValue';
import {
  AGENT_REPORT_LABELS,
  type AgentReportColumn,
  missingAgentMetricPeriods,
} from 'constants/agentReporting';
import React from 'react';
import { Cell, Pie, PieChart, ResponsiveContainer, Tooltip } from 'recharts';
import styles from './AgentActivityWidget.module.scss';
import type { AgentActivityReport } from './agentActivityData';

const PROVIDERS: Record<string, { name: string; color: string }> = {
  codex: { name: 'Codex', color: 'var(--theme_color_solid_primary)' },
  claude_code: { name: 'Claude Code', color: 'var(--theme_color_solid_success)' },
  claude_cowork: { name: 'Claude Cowork', color: 'var(--reference_palette_purple_50)' },
  chatgpt: { name: 'ChatGPT', color: 'var(--theme_color_solid_alert)' },
};

export default function AgentActivityDonut({
  data,
  metric,
}: {
  data: AgentActivityReport;
  metric: AgentReportColumn;
}) {
  const rows = (data.breakdowns.providers || []).map(provider => ({
    ...provider,
    name: PROVIDERS[provider.id]?.name || provider.name || provider.id,
    color: PROVIDERS[provider.id]?.color || 'var(--theme_color_solid_strong)',
    value: provider.metrics[metric],
  }));
  const slices = rows.filter(row => row.value != null && row.value > 0);
  const measuredTotal = slices.reduce((total, row) => total + (row.value || 0), 0);
  const label = AGENT_REPORT_LABELS[metric];
  const isPartial = missingAgentMetricPeriods(data.totals, metric);
  return (
    <section className={styles.body} aria-label="Agent activity by agent">
      <div className={styles.donutLayout}>
        <div className={styles.donutGraphic}>
          {slices.length > 0 ? (
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie
                  data={slices}
                  dataKey="value"
                  nameKey="name"
                  innerRadius="68%"
                  outerRadius="90%"
                  startAngle={90}
                  endAngle={-270}
                  paddingAngle={slices.length > 1 ? 2 : 0}
                  stroke="none"
                  isAnimationActive={false}
                >
                  {slices.map(row => (
                    <Cell key={row.id} fill={row.color} />
                  ))}
                </Pie>
                <Tooltip
                  content={({ active, payload }) => {
                    const row = payload?.[0]?.payload;
                    return active && row ? (
                      <div className={styles.tooltip}>
                        <strong>{row.name}</strong>
                        <p>
                          {label}: {formatAgentReportValue(row.metrics, metric)}
                        </p>
                      </div>
                    ) : null;
                  }}
                />
              </PieChart>
            </ResponsiveContainer>
          ) : (
            <div className={styles.emptyDonut} />
          )}
          <div className={styles.donutTotal}>
            <strong>
              <AgentReportValue metrics={data.totals} column={metric} link={false} />
            </strong>
            <span>{label}</span>
          </div>
        </div>
        <div className={styles.donutLegend}>
          <h4>By agent</h4>
          <table aria-label="Agent activity breakdown">
            <thead>
              <tr>
                <th scope="col">Agent</th>
                <th scope="col">{label}</th>
                <th scope="col">Share{isPartial ? ' of measured' : ''}</th>
              </tr>
            </thead>
            <tbody>
              {rows.map(row => (
                <tr key={row.id}>
                  <th scope="row">
                    <svg width="10" height="10" aria-hidden="true">
                      <circle cx="5" cy="5" r="5" fill={row.color} />
                    </svg>
                    {row.name}
                  </th>
                  <td>
                    <AgentReportValue metrics={row.metrics} column={metric} />
                  </td>
                  <td>
                    {row.value == null || measuredTotal === 0
                      ? '—'
                      : `${((row.value / measuredTotal) * 100).toFixed(1)}%`}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          {!!isPartial && (
            <p>
              Missing measurements are excluded from the donut. Shares reflect measured activity
              only.
            </p>
          )}
          {slices.length === 0 && (
            <p>
              {rows.some(row => row.value == null)
                ? 'No measured values to chart.'
                : 'No activity for this metric.'}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
